import {createContext, JSX, useEffect, useMemo, useState} from 'react';
import { AudienceModalState } from  '@/interfaces/ContentAudienceModal';
import Toast, { IToastVariables, ToastMessageType } from '../global/ToastNotification';
import useDebounce from  '@/components/hooks/useDebounce';
import { ITableContent } from  '@/interfaces/simpleTable';
import { IAudienceCounts, IGroups, IUsers } from  '@/interfaces/api/ContentAudience';
import { getAudienceCounts as getAudienceCountsApi } from '@/api/contentAudience';

interface IContentAudienceContext {
    appId: string,
    contentId: string,
    disableModal: boolean,
    setDisableModal: (disable: boolean) => void;
    audienceModalState: AudienceModalState | undefined;
    setAudienceModalState: (state: AudienceModalState | undefined) => void;
    currentSelection: 'Groups' | 'Users';
    setCurrentSelection: (newSelection: 'Groups' | 'Users') => void;
    searchValue: string;
    setSearchValue: (searchTerm: string) => void;
    filterOptions: string[];
    setFilterOptions: (newOptions: string[]) => void;
    filterIsOpen: boolean;
    setFilterIsOpen: (change: boolean) => void ;
    contentTitle: string;
    setContentTitle: (title: string) => void;
    setToast:({type, message}:{type: ToastMessageType, message: string}) => void;
    debouncedSearchTerm: '';
    sort: {sort: string, sortBy:string}
    setSort: ({sort, sortBy}: {sort: string, sortBy:string}) => void
    currentSkip: number,
    setCurrentSkip: (newSkip: number) => void,
    totalRecords: number,
    setTotalRecords: (newTotal: number) => void,
    table: ITableContent,
    setTable: (table: ITableContent) => void,
    showExistsInMultiplePlaces: boolean,
    addToExistsMessageSeen: () => void,
    showParentModal: () => void,
    lastPage: AudienceModalState | undefined,
    currentSelectionChanged: boolean,
    setCurrentSelectionChanged: (val: boolean) => void,
    audienceCounts: IAudienceCounts,
    setAudienceCounts: (newCount: IAudienceCounts) => void,
    usersOrGroupsLoading: boolean,
    setUsersOrGroupsLoading: (val: boolean) => void,
    getAudienceList: any,
    groupFilters: string[],
    setGroupFilters: (newFilters: string[]) => void,
    isOrgAdmin: boolean,
    isEditor: boolean,
    updatingUserOrGroupIds: number[],
    setUpdatingUserOrGroupIds: (val: number[]) => void 
    getAudienceCounts: () => void;
    audienceCountsLoading:boolean
    list: any,
    setList: any
}

export const ContentAudienceContext = createContext<IContentAudienceContext>({
    appId: '',
    contentId: '',
    audienceModalState: undefined,
    setDisableModal:() => {},
    disableModal: false,
    setAudienceModalState: () => {},
    currentSelection: 'Groups',
    setCurrentSelection: () => {},
    searchValue:'',
    setSearchValue: () => {},
    filterOptions: [],
    setFilterOptions: () => {},
    filterIsOpen: false,
    setFilterIsOpen: () => {},
    contentTitle: '',
    setContentTitle: () => {},
    setToast:() => {},
    debouncedSearchTerm: '',
    sort: {sortBy: 'groupsName', sort: 'asc'},
    setSort: () => {},
    currentSkip: 0,
    setCurrentSkip: () => {},
    totalRecords: 0,
    setTotalRecords: () => {},
    table: {columns :[], rows: []},
    setTable: () => {},
    showExistsInMultiplePlaces: true,
    addToExistsMessageSeen: () => {},
    showParentModal: () => {},
    lastPage: undefined,
    currentSelectionChanged: false,
    setCurrentSelectionChanged: () => {},
    audienceCounts: {groupCount: 0, editorCount: 0, audienceCount: 0, everyoneHasAccess: false},
    setAudienceCounts: () => {},
    usersOrGroupsLoading: false,
    setUsersOrGroupsLoading: () => {},
    getAudienceList: undefined,
    groupFilters: [],
    setGroupFilters: () => {},
    isEditor: true,
    isOrgAdmin: true,
    updatingUserOrGroupIds: [],
    setUpdatingUserOrGroupIds: () => {},
    getAudienceCounts: () => {},
    audienceCountsLoading: false,
    list: [],
    setList: () => {}
});


export default function ContentAudienceProvider({appId, existsInMultipleLocations, isOrgAdmin, isEditor, children} : {appId: string, existsInMultipleLocations: boolean, children: JSX.Element, isOrgAdmin: boolean, isEditor: boolean}) {
    const [audienceModalState, setAudienceModalState] = useState<AudienceModalState>();
    const [currentSelection, setCurrentSelection ] = useState<'Groups' | 'Users'>('Groups');
    const [searchValue, setSearchValue] = useState<string>('')
    const [filterOptions, setFilterOptions] = useState<string[]>([]);
    const [groupFilters, setGroupFilters] = useState<string[]>([]);
    const [filterIsOpen, setFilterIsOpen] = useState<boolean>(false);
    const [contentTitle, setContentTitle] = useState<string>((document?.querySelector('#Title') as HTMLInputElement)?.value || '');
    const [sort, setSort] = useState<any>({sortBy: 'groupsName', sort: 'asc'})
    const [contentId, setContentId] = useState<string>((document.querySelector('#Id') as HTMLInputElement)?.value || '')
    const [table, setTable] = useState<ITableContent>({columns :[], rows: []}) 
    const [audienceCounts, setAudienceCounts ] = useState<IAudienceCounts>({groupCount: 0, editorCount: 0, audienceCount: 0, everyoneHasAccess: false}) 
    const [existingAudienceUsers, setExistingAudienceUsers] = useState<IUsers[]>([])
    const [existingAudienceGroups, setExistingAudienceGroups] = useState<IGroups[]>([])
    const [addAudienceGroups, setAddAudienceGroups] = useState<IGroups[]>([]);
    const [audienceCountsLoading, setAudienceCountsLoading] = useState<boolean>(true);
    const [disableModal, setDisableModal] = useState<boolean>(false);
    const [toastVariables, setToastVariables] = useState<IToastVariables>();
    const [showToast, setShowToast] = useState<boolean>();
    const [currentSkip, setCurrentSkip] = useState<number>(0)
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [showExistsInMultiplePlaces, setShowExistsInMultiplePlaces] = useState<boolean>(showExistsInMultipleLocationsInfo())
    const [lastPage, setLastPage] = useState<AudienceModalState>();

    const [usersOrGroupsLoading, setUsersOrGroupsLoading] = useState<boolean>(false);

    const listIdObserver = new MutationObserver((mutation: any[]) => setContentId(() => {
      return mutation[0].target.value
    }));

    const listTitleObserver = new MutationObserver((mutation: any[]) => setContentTitle(mutation[0].target.value)); 

    let debouncedSearchTerm = useDebounce(searchValue, 500)

    const [currentSelectionChanged, setCurrentSelectionChanged] = useState<boolean>(false) 

    const [updatingUserOrGroupIds, setUpdatingUserOrGroupIds] = useState<number[]>([]) 

    const [list, setList] = useMemo(() => getAudienceList(), [audienceModalState, currentSelection, usersOrGroupsLoading])

    useEffect(() => {
        setCurrentSelectionChanged(true)
        setFilterOptions([]);
        setSearchValue('');
        setFilterIsOpen(false);
        setSort(setDefaultFilters())
        debouncedSearchTerm = ''
        setCurrentSkip(0);
        setTotalRecords(0);
        setGroupFilters([]);
        setUpdatingUserOrGroupIds([]);
        
    }, [currentSelection, audienceModalState])

    const initialContentId = (document?.querySelector('#Id') as HTMLInputElement)?.value;

    useEffect(() => {
      if(initialContentId) {
        setContentId(initialContentId)
      }
     
    },[initialContentId])

    useEffect(() => {
        /* hidden read only id field exists on page, because its read only we cannot bind an on change event like you would expect, so we have to use a mutation observer
          this will update when a content id is set on save or save and publish
        */
        if(document.querySelector('#Id')) {
          listIdObserver.observe(document.querySelector('#Id') as HTMLInputElement, {attributes: true, childList: true})
        }

        if(document.querySelector('#Title')) {
            listTitleObserver.observe(document.querySelector('#Title') as HTMLInputElement, {attributes: true, childList: true})
          }
      }, []);

      useEffect(() => {
        if(Number(contentId)) {
            getAudienceCounts();
        }
    }, [contentId])

      function getAudienceList(){
        if(audienceModalState === AudienceModalState.EditAudience) {
          setAddAudienceGroups([]);

          if(currentSelection === 'Groups') {
            setExistingAudienceUsers([])
            return [existingAudienceGroups, setExistingAudienceGroups]
          }
          else {
            setExistingAudienceGroups([]);
            return [existingAudienceUsers, setExistingAudienceUsers]
          }
        }
        else {
          setExistingAudienceGroups([])
          setExistingAudienceUsers([])
          return [addAudienceGroups, setAddAudienceGroups]
        }
      }

      function setDefaultFilters() {
        if(audienceModalState === AudienceModalState.AddAudience) {
          return {sortBy: currentSelection === 'Groups' ? 'groupsName' : 'userName', sort: 'asc'}
        }

        return {sortBy: 'permissions', sort: 'desc'}
      }

      function setToast(message: {type: ToastMessageType, message: string}) {
        setShowToast(true);
        setToastVariables({messageType: message.type, message: message.message })
      }

      function getSeenByArrayFromStorage(){
        return JSON.parse(window.sessionStorage.getItem('existsInMultpleSeenby') || '[]');
      }

      function showExistsInMultipleLocationsInfo(){
        const seenBy:string[] = getSeenByArrayFromStorage()
        
        return !seenBy.includes(contentId) && existsInMultipleLocations;
      }

      function addToExistsMessageSeen() {
        const seenBy:string[] = getSeenByArrayFromStorage();
        if(!seenBy.includes(contentId)) {
            seenBy.push(contentId)
        }
        window.sessionStorage.setItem('existsInMultpleSeenby', JSON.stringify(seenBy))
        setShowExistsInMultiplePlaces(false);
      }

      function showParentModal(){
        setLastPage(audienceModalState)
        setAudienceModalState(AudienceModalState.ViewParents)
      }

      function getAudienceCounts() {
        getAudienceCountsApi(contentId)
        .then(data => {setAudienceCounts(data)})
        .finally(() => setAudienceCountsLoading(false));
    }

    const contextValues: IContentAudienceContext = useMemo(() => ({
        appId,
        contentId,
        audienceModalState,
        setAudienceModalState,
        currentSelection,
        setCurrentSelection,
        searchValue,
        setSearchValue,
        filterOptions,
        setFilterOptions,
        filterIsOpen,
        setFilterIsOpen,
        contentTitle,
        setContentTitle,
        setToast,
        debouncedSearchTerm,
        sort,
        setSort,
        currentSkip,
        setCurrentSkip,
        totalRecords,
        setTotalRecords,
        table,
        setTable,
        showExistsInMultiplePlaces,
        addToExistsMessageSeen,
        showParentModal,
        lastPage,
        currentSelectionChanged,
        setCurrentSelectionChanged,
        audienceCounts,
        setAudienceCounts,
        usersOrGroupsLoading,
        setUsersOrGroupsLoading,
        getAudienceList,
        groupFilters,
        setGroupFilters,
        isOrgAdmin,
        isEditor,
        updatingUserOrGroupIds,
        setUpdatingUserOrGroupIds,
        getAudienceCounts,
        audienceCountsLoading,
        list,
        setList,
        disableModal,
        setDisableModal
    }),[
        appId,
        contentId,
        audienceModalState,
        currentSelectionChanged,
        currentSelection,
        searchValue,
        filterOptions,
        filterIsOpen,
        contentTitle,
        debouncedSearchTerm,
        sort,
        currentSkip,
        totalRecords,
        table,
        showExistsInMultiplePlaces,
        lastPage,
        audienceCounts,
        usersOrGroupsLoading,
        groupFilters, 
        isOrgAdmin,
        isEditor,
        updatingUserOrGroupIds,
        list,
        disableModal
    ])

    return (
        <ContentAudienceContext.Provider value={contextValues}>
        {showToast && toastVariables?.messageType &&
            <Toast show={showToast} closeToast={() => setShowToast(false)} messageType={toastVariables.messageType}> {toastVariables?.message}</Toast>
        }
            {children}
        </ContentAudienceContext.Provider>
    )
}